import styles from "./SimpleTable.module.scss";

interface SimpleTableColumn<T> {
    /**
     * The header text for the column.
     */
    headerText: string;

    /**
     * The key used to access the data for this column.
     */
    accessor: string;

    /**
     * An optional component to render the cell content for this column.
     * The component receives the item being rendered in the cell as a prop.
     */
    cellRenderer?: React.ComponentType<{ item: T }>;
}

/**
 * MedicationTableProps defines the properties for the Simple component.
 */
interface SimpleTableProps<T> {
    /**
     * A unique key prefix for the table, used for generating unique keys for elements.
     */
    keyPrefix: string;

    /**
     * An array of items to be displayed in the table.
     */
    data: T[];

    /**
     * An array of column definitions for the table, each containing header text and an accessor for the data.
     */
    columns: SimpleTableColumn<T>[];

    /**
     * function that returns the unique ID of the item.
     */
    getId: (item: T) => string | number;
}

/**
 * SimpleTable renders a table for displaying data.
 */
function SimpleTable<T>({ keyPrefix, data, columns, getId }: Readonly<SimpleTableProps<T>>): JSX.Element {
    return (
        <table className={styles.simpleTable}>
            <thead>
                <tr
                    className={styles.simpleTableRow}
                >
                    {columns.map(column => (
                        <th
                            key={`${keyPrefix} ${String(column.accessor)}`}
                            className={styles.simpleTableHeaderCell}
                        >
                            {column.headerText}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {data.map(item => (
                    <tr
                        key={`${keyPrefix} ${getId(item)}`}
                        className={styles.simpleTableRow}
                    >
                        {columns.map(column => (
                            <td
                                key={`${keyPrefix} ${String(column.accessor)}_${getId(item)}`}
                                className={styles.simpleTableCell}
                            >
                                {column.cellRenderer ? (
                                    <column.cellRenderer item={item} />
                                ) : (
                                    item[column.accessor]
                                )
                                }
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export default SimpleTable;

export {
    SimpleTableColumn,
    SimpleTableProps
};
